import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from './shell/sidebar/sidebar.component';
import { HeaderComponent } from './shell/header/header.component';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { catchError, filter, of, Subject, takeUntil } from 'rxjs';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { AuthService } from './shared/services/auth.service';
import { ICredentials } from './shared/models/credentials.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { InfoDialogComponent } from './shared/components/info-dialog/info-dialog.component';
import { CreateCasePopupComponent } from './shared/components/create-case-popup/create-case-popup.component';
import { HelperService } from './shared/services/helper.service';
import { DiscardChangesService } from './shared/services/discard-changes.service';
import { LoadingSpinnerComponent } from './shared/components/loading-spinner/loading-spinner.component';
import { LoaderService } from './shared/services/loader.service';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbComponent } from "./shared/components/breadcrumb/breadcrumb.component";
import { NoCookiesService } from './shared/services/no-cookies.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [SidebarComponent, HeaderComponent, CommonModule, CreateCasePopupComponent, LoadingSpinnerComponent, RouterOutlet, BreadcrumbComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'edwards-cms';
  isIframe = false;
  isUserLogin: boolean = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    public authService: AuthService,
    public helperService: HelperService,
    private modalService: NgbModal,
    private translate: TranslateService,
    public loader: LoaderService,
    private noCookiesService: NoCookiesService,
  ) {
    this.authService.setCredentials(this.authService.getCredentialsFromStorage());

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
      )
      .subscribe((result: EventMessage) => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          console.log('Not logged-in');
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroy$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
      })
  }

  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener;
    this.msalService.handleRedirectObservable().subscribe();
    this.msalService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
  }

  setLoginDisplay() {
    this.isUserLogin = this.msalService.instance.getAllAccounts().length > 0;

    if (this.isUserLogin) {
      const profile_data = this.msalService.instance.getAllAccounts()[0];
      this.msalService.instance.setActiveAccount(profile_data);
      this.getProfile();
    } else {
      console.log('Not logged-in');
    }
  }

  getProfile() {
    if (this.isUserLogin && !this.authService.credentials)
      this.authService
        .getUserContext()
        .pipe(takeUntil(this._destroy$),
          catchError(err => {
            return of(err);
          })
        )
        .subscribe((data: ICredentials) => {
          if (data.role) {
            this.authService.setCredentials(data);
          } else {
            this.openInfoDialog(this.translate.instant('label.access_denied'), this.translate.instant('messages.user_unable_to_access_message'), this.translate.instant('buttons.close'));
          }
        });
  }

  openInfoDialog(
    title: string,
    message: string,
    confirmLabel: string
  ): void {
    const modalRef = this.modalService.open(InfoDialogComponent, {
      modalDialogClass: 'confirmation-modal',
      backdrop: 'static',
      keyboard: false
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.confirmActionLabel = confirmLabel;

    modalRef.result.then(() => this.authService.logout());
  }

  ngOnDestroy(): void {
    this._destroy$.next(undefined);
    this._destroy$.complete();
  }

}
